import { useCallback, useRef } from 'react';

/** Pointer handlers for chart crosshair / tooltip scrub (touch + mouse). */
export interface ChartScrubHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
  onPointerLeave: (e: React.PointerEvent) => void;
}

const SCROLL_CANCEL_PX = 10;
const SCRUB_START_PX = 8;

/**
 * Chart touch + mouse interaction:
 * - Touch/pen: tap a bar/point to show data; drag horizontally to scrub; tooltip stays after lift.
 * - Mouse: hover to scrub; clear on pointer leave.
 * - Touch vertical movement cancels (allows page scroll); pointer capture only after horizontal scrub.
 */
export function useChartScrub(
  enabled: boolean,
  onScrub: (clientX: number) => void,
  onScrubEnd?: () => void,
  allowTouchScrub = true,
): ChartScrubHandlers {
  const scrubbingRef = useRef(false);
  const armedRef = useRef(false);
  const capturedRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0 });

  const releaseCapture = useCallback((e: React.PointerEvent) => {
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
  }, []);

  const endScrub = useCallback((clear = true) => {
    scrubbingRef.current = false;
    armedRef.current = false;
    capturedRef.current = false;
    if (clear) onScrubEnd?.();
  }, [onScrubEnd]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled) return;
      if (e.pointerType === 'mouse') {
        scrubbingRef.current = true;
        onScrub(e.clientX);
        return;
      }
      armedRef.current = true;
      scrubbingRef.current = false;
      capturedRef.current = false;
      startRef.current = { x: e.clientX, y: e.clientY };
      if (allowTouchScrub) onScrub(e.clientX);
    },
    [enabled, onScrub, allowTouchScrub],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled) return;
      if (e.pointerType === 'mouse') {
        onScrub(e.clientX);
        return;
      }
      if (!armedRef.current && !scrubbingRef.current) return;

      const dx = e.clientX - startRef.current.x;
      const dy = e.clientY - startRef.current.y;

      if (!allowTouchScrub) {
        if (Math.hypot(dx, dy) > SCROLL_CANCEL_PX) endScrub(true);
        return;
      }

      if (!scrubbingRef.current) {
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > SCROLL_CANCEL_PX) {
          endScrub(true);
          return;
        }
        if (Math.abs(dx) <= SCRUB_START_PX || Math.abs(dx) < Math.abs(dy)) return;

        scrubbingRef.current = true;
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
          capturedRef.current = true;
        } catch {
          /* capture unsupported */
        }
      }

      onScrub(e.clientX);
    },
    [enabled, onScrub, endScrub, allowTouchScrub],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled) return;
      if (!armedRef.current && !scrubbingRef.current) return;

      onScrub(e.clientX);
      if (capturedRef.current) releaseCapture(e);

      if (e.pointerType === 'mouse') {
        endScrub(true);
        return;
      }

      // Touch/pen: keep tooltip visible after tap or scrub.
      armedRef.current = false;
      scrubbingRef.current = false;
      capturedRef.current = false;
    },
    [enabled, onScrub, endScrub, releaseCapture],
  );

  const onPointerCancel = useCallback(() => {
    endScrub();
  }, [endScrub]);

  const onPointerLeave = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === 'mouse') endScrub();
    },
    [endScrub],
  );

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onPointerLeave };
}
